const GLITCH_STYLE_ID = "er-text-effect-styles";

export const TEXT_EFFECT_OPTIONS = [
	{ id: "none", label: "Ninguno" },
	{ id: "shadow", label: "Sombreado" },
	{ id: "glow", label: "Resplandor" },
	{ id: "glitch", label: "Glitch" },
];

export const DEFAULT_TEXT_EFFECT_SHADOW = {
	offsetX: 2,
	offsetY: 2,
	blur: 8,
	color: "rgba(0, 0, 0, 0.5)",
};

export const DEFAULT_TEXT_EFFECT_GLOW = {
	intensity: 70,
	color: "#7c3aed",
};

export const DEFAULT_TEXT_EFFECT_GLITCH = {
	colorA: "#ff00c8",
	colorB: "#00e5ff",
	intensity: 70,
	speed: 50,
};

function clamp(n, min, max) {
	return Math.min(max, Math.max(min, n));
}

function parseColorToRgb(str) {
	if (str == null || str === "transparent") return null;
	const s = String(str).trim();
	const rgba = s.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
	if (rgba) return { r: +rgba[1], g: +rgba[2], b: +rgba[3], a: rgba[4] !== undefined ? +rgba[4] : 1 };
	const hex = s.match(/^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/);
	if (hex) {
		let h = hex[1];
		if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
		return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16), a: 1 };
	}
	return null;
}

const EFFECT_KEY_BY_KIND = {
	text: { effect: "textEffect", shadow: "textEffectShadow", glow: "textEffectGlow", glitch: "textEffectGlitch" },
	icon: { effect: "iconEffect", shadow: "iconEffectShadow", glow: "iconEffectGlow", glitch: "iconEffectGlitch" },
	frame: { effect: "frameEffect", shadow: "frameEffectShadow", glow: "frameEffectGlow", glitch: "frameEffectGlitch" },
	optionBox: { effect: "optionBoxEffect", shadow: "optionBoxEffectShadow", glow: "optionBoxEffectGlow", glitch: "optionBoxEffectGlitch" },
};

/** Lee/escribe efectos con prefijo según destino: text (style), icon (content), frame (style). */
export function pickEffectBundle(obj = {}, kind = "text") {
	const k = EFFECT_KEY_BY_KIND[kind] || EFFECT_KEY_BY_KIND.text;
	const source = obj || {};
	return {
		textEffect: source[k.effect],
		textEffectShadow: source[k.shadow],
		textEffectGlow: source[k.glow],
		textEffectGlitch: source[k.glitch],
		color: source.color,
	};
}

export function mergeEffectBundlePatch(obj = {}, kind = "text", patch = {}) {
	const k = EFFECT_KEY_BY_KIND[kind] || EFFECT_KEY_BY_KIND.text;
	const next = { ...(obj || {}) };
	if (Object.prototype.hasOwnProperty.call(patch, "textEffect")) next[k.effect] = patch.textEffect;
	if (Object.prototype.hasOwnProperty.call(patch, "textEffectShadow")) next[k.shadow] = patch.textEffectShadow;
	if (Object.prototype.hasOwnProperty.call(patch, "textEffectGlow")) next[k.glow] = patch.textEffectGlow;
	if (Object.prototype.hasOwnProperty.call(patch, "textEffectGlitch")) next[k.glitch] = patch.textEffectGlitch;
	return next;
}

export function hasActiveTextEffect(style = {}) {
	const effect = style.textEffect || "none";
	return !!effect && effect !== "none";
}

export function hasActiveEffectBundle(obj = {}, kind = "text") {
	return hasActiveTextEffect(pickEffectBundle(obj, kind));
}

export function buttonElementNeedsEffectOverflow(element = {}) {
	const s = element.style || {};
	const c = element.content || {};
	return (
		!!element.content?.continuousPulse ||
		hasActiveEffectBundle(s, "text") ||
		hasActiveEffectBundle(c, "icon") ||
		hasActiveEffectBundle(s, "frame")
	);
}

export function normalizeTextEffectShadow(raw) {
	const s = raw || {};
	return {
		offsetX: Number.isFinite(Number(s.offsetX)) ? Number(s.offsetX) : DEFAULT_TEXT_EFFECT_SHADOW.offsetX,
		offsetY: Number.isFinite(Number(s.offsetY)) ? Number(s.offsetY) : DEFAULT_TEXT_EFFECT_SHADOW.offsetY,
		blur: clamp(Number.isFinite(Number(s.blur)) ? Number(s.blur) : DEFAULT_TEXT_EFFECT_SHADOW.blur, 0, 48),
		color: String(s.color || DEFAULT_TEXT_EFFECT_SHADOW.color).trim() || DEFAULT_TEXT_EFFECT_SHADOW.color,
	};
}

export function normalizeTextEffectGlow(raw, fallbackTextColor) {
	const g = raw || {};
	let color = String(g.color || "").trim();
	if (!color && fallbackTextColor) color = String(fallbackTextColor).trim();
	return {
		intensity: clamp(Number.isFinite(Number(g.intensity)) ? Number(g.intensity) : DEFAULT_TEXT_EFFECT_GLOW.intensity, 0, 100),
		color: color || DEFAULT_TEXT_EFFECT_GLOW.color,
	};
}

export function normalizeTextEffectGlitch(raw) {
	const g = raw || {};
	return {
		colorA: String(g.colorA || DEFAULT_TEXT_EFFECT_GLITCH.colorA).trim() || DEFAULT_TEXT_EFFECT_GLITCH.colorA,
		colorB: String(g.colorB || DEFAULT_TEXT_EFFECT_GLITCH.colorB).trim() || DEFAULT_TEXT_EFFECT_GLITCH.colorB,
		intensity: clamp(Number.isFinite(Number(g.intensity)) ? Number(g.intensity) : DEFAULT_TEXT_EFFECT_GLITCH.intensity, 0, 100),
		speed: clamp(Number.isFinite(Number(g.speed)) ? Number(g.speed) : DEFAULT_TEXT_EFFECT_GLITCH.speed, 0, 100),
	};
}

export function buildGlitchStaticTextShadow(style = {}) {
	const g = normalizeTextEffectGlitch(style.textEffectGlitch);
	const px = (1 + (g.intensity / 100) * 3).toFixed(1);
	return `${px}px 0 ${g.colorA}, -${px}px 0 ${g.colorB}`;
}

export function getGlitchEffectCssVars(style = {}) {
	const g = normalizeTextEffectGlitch(style.textEffectGlitch);
	const px = (1 + (g.intensity / 100) * 3).toFixed(1);
	const colorDur = (0.12 + (1 - g.speed / 100) * 0.58).toFixed(2);
	const shiftDur = (1 + (1 - g.speed / 100) * 2.4).toFixed(2);
	return {
		"--er-glitch-a": g.colorA,
		"--er-glitch-b": g.colorB,
		"--er-glitch-px": `${px}px`,
		"--er-glitch-color-dur": `${colorDur}s`,
		"--er-glitch-shift-dur": `${shiftDur}s`,
	};
}

function buildGlitchStaticFilter(style = {}) {
	const g = normalizeTextEffectGlitch(style.textEffectGlitch);
	const px = (1 + (g.intensity / 100) * 3).toFixed(1);
	return `drop-shadow(${px}px 0 ${g.colorA}) drop-shadow(-${px}px 0 ${g.colorB})`;
}

export function buildShadowTextShadow(style = {}) {
	const sh = normalizeTextEffectShadow(style.textEffectShadow);
	return `${sh.offsetX}px ${sh.offsetY}px ${sh.blur}px ${sh.color}`;
}

export function buildGlowTextShadow(style = {}, fallbackTextColor) {
	const g = normalizeTextEffectGlow(style.textEffectGlow, fallbackTextColor || style.color);
	const rgb = parseColorToRgb(g.color) || { r: 124, g: 58, b: 237 };
	const t = g.intensity / 100;
	const b1 = 2 + t * 8;
	const b2 = 6 + t * 18;
	const b3 = 12 + t * 28;
	const alpha = 0.25 + t * 0.45;
	return `0 0 ${b1}px ${g.color}, 0 0 ${b2}px ${g.color}, 0 0 ${b3}px rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

function buildContainerGlowFilter(style = {}, fallbackTextColor) {
	const g = normalizeTextEffectGlow(style.textEffectGlow, fallbackTextColor || style.color);
	const rgb = parseColorToRgb(g.color) || { r: 124, g: 58, b: 237 };
	const t = g.intensity / 100;
	const b1 = 4 + t * 10;
	const b2 = 10 + t * 22;
	return `drop-shadow(0 0 ${b1}px ${g.color}) drop-shadow(0 0 ${b2}px rgba(${rgb.r},${rgb.g},${rgb.b},0.55))`;
}

function buildContainerShadowFilter(style = {}) {
	const sh = normalizeTextEffectShadow(style.textEffectShadow);
	return `drop-shadow(${sh.offsetX}px ${sh.offsetY}px ${sh.blur}px ${sh.color})`;
}

function buildContainerShadowBoxShadow(style = {}) {
	const sh = normalizeTextEffectShadow(style.textEffectShadow);
	return `${sh.offsetX}px ${sh.offsetY}px ${sh.blur}px ${sh.color}`;
}

export function mergeBoxShadow(base, addition) {
	if (!addition) return base || undefined;
	if (!base || base === "none") return addition;
	return `${base}, ${addition}`;
}

export function getTextEffectPreviewStyle(effectId, style = {}) {
	if (!effectId || effectId === "none") return { color: "#1e293b" };
	if (effectId === "shadow") {
		return { color: "#1e293b", textShadow: buildShadowTextShadow(style) };
	}
	if (effectId === "glow") {
		return { color: "#1e293b", textShadow: buildGlowTextShadow(style, style.color) };
	}
	if (effectId === "glitch") {
		return { color: "#1e293b", textShadow: buildGlitchStaticTextShadow(style) };
	}
	return { color: "#1e293b" };
}

export function ensureTextEffectStylesInjected() {
	if (typeof document === "undefined") return;
	let el = document.getElementById(GLITCH_STYLE_ID);
	if (!el) {
		el = document.createElement("style");
		el.id = GLITCH_STYLE_ID;
		document.head.appendChild(el);
	}
	el.textContent = `
@keyframes er-text-glitch-shift {
	0%, 45%, 100% { transform: translate(0); }
	10% { transform: translate(calc(-1 * var(--er-glitch-px, 2px)), var(--er-glitch-px, 2px)); }
	20% { transform: translate(var(--er-glitch-px, 2px), calc(-1 * var(--er-glitch-px, 2px))); }
	30% { transform: translate(calc(-0.5 * var(--er-glitch-px, 2px)), calc(-1 * var(--er-glitch-px, 2px))); }
	40% { transform: translate(var(--er-glitch-px, 2px), var(--er-glitch-px, 2px)); }
}
@keyframes er-text-glitch-color {
	0%, 100% { text-shadow: var(--er-glitch-px, 2px) 0 var(--er-glitch-a, #ff00c8), calc(-1 * var(--er-glitch-px, 2px)) 0 var(--er-glitch-b, #00e5ff); }
	33% { text-shadow: calc(-1 * var(--er-glitch-px, 2px)) 0 var(--er-glitch-a, #ff00c8), var(--er-glitch-px, 2px) 0 var(--er-glitch-b, #00e5ff); }
	66% { text-shadow: var(--er-glitch-px, 2px) var(--er-glitch-px, 2px) var(--er-glitch-a, #ff00c8), calc(-1 * var(--er-glitch-px, 2px)) calc(-1 * var(--er-glitch-px, 2px)) var(--er-glitch-b, #00e5ff); }
}
@keyframes er-element-glitch-filter {
	0%, 100% { filter: drop-shadow(var(--er-glitch-px, 2px) 0 var(--er-glitch-a, #ff00c8)) drop-shadow(calc(-1 * var(--er-glitch-px, 2px)) 0 var(--er-glitch-b, #00e5ff)); }
	33% { filter: drop-shadow(calc(-1 * var(--er-glitch-px, 2px)) 0 var(--er-glitch-a, #ff00c8)) drop-shadow(var(--er-glitch-px, 2px) 0 var(--er-glitch-b, #00e5ff)); }
	66% { filter: drop-shadow(var(--er-glitch-px, 2px) var(--er-glitch-px, 2px) var(--er-glitch-a, #ff00c8)) drop-shadow(calc(-1 * var(--er-glitch-px, 2px)) calc(-1 * var(--er-glitch-px, 2px)) var(--er-glitch-b, #00e5ff)); }
}
.er-text-effect-glitch {
	--er-glitch-a: #ff00c8;
	--er-glitch-b: #00e5ff;
	--er-glitch-px: 2px;
	--er-glitch-color-dur: 0.4s;
	--er-glitch-shift-dur: 2.6s;
	animation: er-text-glitch-shift var(--er-glitch-shift-dur) infinite steps(2), er-text-glitch-color var(--er-glitch-color-dur) infinite steps(2);
}
.er-element-effect-glitch {
	--er-glitch-a: #ff00c8;
	--er-glitch-b: #00e5ff;
	--er-glitch-px: 2px;
	--er-glitch-color-dur: 0.4s;
	--er-glitch-shift-dur: 2.6s;
	animation: er-text-glitch-shift var(--er-glitch-shift-dur) infinite steps(2), er-element-glitch-filter var(--er-glitch-color-dur) infinite steps(2);
}
.er-static-preview .er-text-effect-glitch,
.er-static-preview .er-element-effect-glitch,
.er-static-preview .er-editor-btn-heartbeat {
	animation: none !important;
}
`;
}

export function getTextEffectPresentation(style = {}, options = {}) {
	const { animated = true, baseClass = "element-text-content" } = options;
	const effect = style.textEffect || "none";
	const className = baseClass || undefined;
	if (!effect || effect === "none") {
		return { className: className || "", extraStyle: {} };
	}
	if (effect === "shadow") {
		return {
			className: className || "",
			extraStyle: { textShadow: buildShadowTextShadow(style) },
		};
	}
	if (effect === "glow") {
		return {
			className: className || "",
			extraStyle: { textShadow: buildGlowTextShadow(style, style.color) },
		};
	}
	if (effect === "glitch") {
		const glitchVars = getGlitchEffectCssVars(style);
		if (animated) {
			ensureTextEffectStylesInjected();
			return {
				className: [className, "er-text-effect-glitch"].filter(Boolean).join(" "),
				extraStyle: glitchVars,
			};
		}
		return {
			className: className || "",
			extraStyle: { textShadow: buildGlitchStaticTextShadow(style) },
		};
	}
	return { className: className || "", extraStyle: {} };
}

export function getElementEffectPresentation(style = {}, options = {}) {
	const { animated = true, preferBoxShadow = false } = options;
	const effect = style.textEffect || "none";
	if (!effect || effect === "none") {
		return { className: "", extraStyle: {} };
	}
	if (effect === "shadow") {
		if (preferBoxShadow) {
			return { className: "", extraStyle: { boxShadow: buildContainerShadowBoxShadow(style) } };
		}
		return { className: "", extraStyle: { filter: buildContainerShadowFilter(style) } };
	}
	if (effect === "glow") {
		return { className: "", extraStyle: { filter: buildContainerGlowFilter(style, style.color) } };
	}
	if (effect === "glitch") {
		const glitchVars = getGlitchEffectCssVars(style);
		if (animated) {
			ensureTextEffectStylesInjected();
			return { className: "er-element-effect-glitch", extraStyle: glitchVars };
		}
		return {
			className: "",
			extraStyle: { filter: buildGlitchStaticFilter(style) },
		};
	}
	return { className: "", extraStyle: {} };
}

export function getButtonEffectPresentation(style = {}, content = {}, options = {}) {
	const { animated = true, disabled = false } = options;
	if (disabled) {
		return {
			buttonClassName: "",
			buttonExtraStyle: {},
			labelClassName: "",
			labelExtraStyle: {},
			iconClassName: "",
			iconExtraStyle: {},
		};
	}
	const frameBundle = { ...pickEffectBundle(style, "frame"), color: style.color };
	const textBundle = { ...pickEffectBundle(style, "text"), color: style.color };
	const iconBundle = { ...pickEffectBundle(content, "icon"), color: style.color };

	const containerFx = getElementEffectPresentation(frameBundle, { animated, preferBoxShadow: true });
	const labelFx = getTextEffectPresentation(textBundle, { animated, baseClass: "" });
	const iconFx = getTextEffectPresentation(iconBundle, { animated, baseClass: "" });
	return {
		buttonClassName: containerFx.className,
		buttonExtraStyle: { ...containerFx.extraStyle },
		labelClassName: labelFx.className,
		labelExtraStyle: { ...labelFx.extraStyle },
		iconClassName: iconFx.className,
		iconExtraStyle: { ...iconFx.extraStyle },
	};
}

export function resolveButtonBorderCss(style = {}, { showHover = false, hasBg = false, disabled = false } = {}) {
	if (disabled) return "1px solid #d1d5db";
	const rawW = style.borderWidth;
	if (rawW != null && rawW !== "" && Number.isFinite(Number(rawW))) {
		const w = Math.max(0, Math.round(Number(rawW)));
		if (w === 0) return "none";
		const hoverBorder = String(style.hoverBorderColor || "").trim();
		const borderCol = showHover && hoverBorder ? hoverBorder : style.borderColor || "rgba(124, 58, 237, 1)";
		return `${w}px solid ${borderCol}`;
	}
	return hasBg ? "none" : "1px dashed rgba(0,0,0,0.2)";
}

/** Marco de imagen: solo si hay grosor explícito; sigue el mismo `borderRadius` del contenedor. */
export function resolveImageBorderCss(style = {}) {
	const rawW = style?.borderWidth;
	if (rawW == null || rawW === "" || !Number.isFinite(Number(rawW))) return "none";
	const w = Math.max(0, Math.round(Number(rawW)));
	if (w === 0) return "none";
	return `${w}px solid ${style.borderColor || "rgba(124, 58, 237, 1)"}`;
}
